/**
 * Картинка заставки: многослойная сцена в низком разрешении (≈640×360),
 * растянутая на экран без сглаживания. Кадр — чистая функция времени:
 * draw(t) всегда рисует одно и то же, поэтому кадры можно снимать по одному.
 */
import moonUrl from "./assets/moon.png";
import mansionUrl from "./assets/mansion.png";
import winUrl from "./assets/mansion_win.png";
import gateUrl from "./assets/gate.png";
import doorsUrl from "./assets/gate_doors.png";
import treeUrl from "./assets/tree.png";
import gravesUrl from "./assets/graves.png";
import logoUrl from "./assets/logo.png";
import logoSmallUrl from "./assets/logo_s.png";
import {
  CUE,
  D0,
  DEPTH,
  LOGO_FLY,
  STRIKES,
  cameraAt,
  candle,
  flashAt,
  hash1,
  smooth,
  strikeLevel,
  type Cam,
} from "./timeline";
import {
  bayer,
  cloudSheet,
  ctx2d,
  emberMask,
  findWindows,
  fogStrip,
  hillsStrip,
  litVariant,
  makeBolt,
  makeCanvas,
  pixelLine,
  ravenSprites,
  rng,
  silhouette,
  tint,
  type Bolt,
  type Canvas,
  type WindowRect,
} from "./pixels";

type Img = HTMLImageElement | Canvas;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.decoding = "async";
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error(`intro: не загрузилось ${url}`));
    im.src = url;
  });
}

interface Raven {
  t0: number;
  x: number;
  y: number;
  d: number;
  vx: number;
  vy: number;
  vd: number;
  flap: number;
  phase: number;
}

interface Debris {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  ember: boolean;
}

/** Лампа в окне: когда загорается, и какое окно — «главное» (силуэт). */
interface WinLight {
  r: WindowRect;
  on: number;
  seed: number;
}

export class IntroScene {
  readonly canvas: Canvas;
  /** prefers-reduced-motion: без тряски камеры. */
  reducedMotion = false;
  private g: CanvasRenderingContext2D;
  private W = 640;
  private H = 360;

  // картинки и их варианты
  private moon!: Img;
  private skyBase: Canvas | null = null;
  private mansion!: Img;
  private mansionLit!: Canvas;
  private mansionSil!: Canvas;
  private win!: Img;
  private winRed!: Canvas;
  private gate!: Img;
  private gateLit!: Canvas;
  private doors!: Img;
  private doorsLit!: Canvas;
  private doorsSil!: Canvas;
  private gateSil!: Canvas;
  private tree!: Img;
  private treeLit!: Canvas;
  private graves!: Img;
  private gravesLit!: Canvas;
  private logoBig!: Img;
  private logoSmall!: Img;
  private logoVariants = new Map<Img, { lit: Canvas; ember: Canvas; red: Canvas; cyan: Canvas; sil: Canvas }>();

  // процедурное
  private clouds!: Canvas;
  private cloudsLit!: Canvas;
  private cloudsHigh!: Canvas;
  private cloudsHighLit!: Canvas;
  private hills!: Canvas;
  private fogs: Canvas[] = [];
  private rain: Canvas[] = [];
  private ravenImg!: Canvas[];
  private ravens: Raven[] = [];
  private circling: Array<{ r: number; ph: number; sp: number; cx: number; cy: number }> = [];
  private bolts = new Map<number, Bolt>();
  private lights: WinLight[] = [];
  private figureWin: WindowRect | null = null;
  private debris: Debris[] = [];
  private vignette: Canvas | null = null;

  constructor() {
    this.canvas = makeCanvas(640, 360);
    this.g = ctx2d(this.canvas);
  }

  /** Загрузка картинок и сборка вариантов. onProgress — доля 0..1. */
  async load(onProgress?: (p: number) => void): Promise<void> {
    const urls = [moonUrl, mansionUrl, winUrl, gateUrl, doorsUrl, treeUrl, gravesUrl, logoUrl, logoSmallUrl];
    let done = 0;
    const imgs = await Promise.all(
      urls.map((u) =>
        loadImage(u).then((im) => {
          done++;
          onProgress?.(done / urls.length);
          return im;
        }),
      ),
    );
    [this.moon, this.mansion, this.win, this.gate, this.doors, this.tree, this.graves, this.logoBig, this.logoSmall] = imgs;

    this.mansionLit = litVariant(this.mansion, 2.1);
    this.mansionSil = silhouette(this.mansion);
    this.winRed = tint(this.win, [0.95, 0.16, 0.12], [30, 0, 4]);
    this.gateLit = litVariant(this.gate, 2.4);
    this.doorsLit = litVariant(this.doors, 2.4);
    this.doorsSil = silhouette(this.doors, [4, 4, 10]);
    this.gateSil = silhouette(this.gate, [4, 4, 10]);
    this.treeLit = litVariant(this.tree, 3);
    this.gravesLit = litVariant(this.graves, 2.2);
    for (const lg of [this.logoBig, this.logoSmall]) {
      this.logoVariants.set(lg, {
        lit: litVariant(lg, 1.6),
        ember: emberMask(lg),
        red: tint(lg, [1, 0.1, 0.1], [90, 0, 0]),
        cyan: tint(lg, [0.1, 0.8, 1], [0, 40, 80]),
        sil: silhouette(lg, [255, 255, 255]),
      });
    }

    this.clouds = cloudSheet(512, 120, 7, 0.36);
    this.cloudsLit = litVariant(this.clouds, 2.6);
    this.cloudsHigh = cloudSheet(512, 70, 19);
    this.cloudsHighLit = litVariant(this.cloudsHigh, 2.6);
    this.hills = hillsStrip(512, 64, 5);
    this.fogs = [
      fogStrip(256, 40, 21, [60, 66, 104], 0.75),
      fogStrip(256, 56, 22, [74, 82, 122], 0.6),
      fogStrip(256, 72, 23, [86, 94, 136], 0.5),
    ];
    this.rain = [this.rainSheet(0.9, 4, 31, [74, 84, 128]), this.rainSheet(0.5, 7, 32, [104, 114, 160]), this.rainSheet(0.22, 12, 33, [140, 150, 196])];
    this.ravenImg = ravenSprites([8, 8, 16], [62, 68, 104]);
    for (const s of STRIKES) if (s.boltX !== null) this.bolts.set(s.seed, makeBolt(s.seed));
    this.setupRavens();
    this.setupWindows();
    this.setupDebris();
  }

  // ─── подготовка ─────────────────────────────────────────────────────

  private rainSheet(density: number, len: number, seed: number, rgb: [number, number, number]): Canvas {
    const c = makeCanvas(256, 256);
    const g = ctx2d(c);
    const r = rng(seed);
    g.fillStyle = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
    const n = Math.round(260 * density);
    for (let i = 0; i < n; i++) {
      const x = Math.floor(r() * 256);
      const y = Math.floor(r() * 256);
      // косые штрихи; рисуем с переносом через край — лист бесшовный
      for (let k = 0; k < len; k++) {
        const px = (x + Math.round(k * 0.22)) & 255;
        const py = (y + k) & 255;
        g.fillRect(px, py, 1, 1);
      }
    }
    return c;
  }

  private setupRavens(): void {
    const r = rng(77);
    this.ravens = [];
    for (let i = 0; i < 17; i++) {
      const side = r() < 0.5 ? -1 : 1;
      this.ravens.push({
        t0: CUE.hitA + 0.04 + r() * 0.45,
        x: (r() - 0.5) * 150,
        y: -120 - r() * 70,
        d: DEPTH.mansion - 4,
        vx: side * (25 + r() * 70),
        vy: -(14 + r() * 40),
        vd: -(20 + r() * 75),
        flap: 8 + r() * 5,
        phase: r() * 10,
      });
    }
    // «герои»: три ворона пролетают у самой камеры поперёк кадра
    const heroes: Array<[number, number, number, number, number, number, number]> = [
      // t0, x, y, d, vx, vy, vd
      [5.35, -60, -125, 110, 180, -10, -60], // на фоне луны
      [CUE.hitA + 1.15, 120, -128, 110, -230, 4, -80],
      [7.05, -150, -135, 105, 160, -12, -55], // во вспышке 7.25
    ];
    for (const [t0, x, y, d, vx, vy, vd] of heroes) {
      this.ravens.push({ t0, x, y, d, vx, vy, vd, flap: 7 + r() * 2, phase: r() * 10 });
    }
    this.circling = [0, 1, 2].map((i) => ({ r: 26 + i * 9, ph: i * 2.1, sp: 0.55 + i * 0.13, cx: -10 + i * 14, cy: -238 - i * 8 }));
  }

  private setupWindows(): void {
    const all = findWindows(this.win).filter((w) => w.area >= 6);
    // «главное» окно — самое крупное во втором этаже по центру
    let best: WindowRect | null = null;
    for (const w of all) {
      if (Math.abs(w.cx - 128) < 20 && w.cy > 100 && w.cy < 170 && (!best || w.area > best.area)) best = w;
    }
    this.figureWin = best;
    // Порядок: сначала главное окно (там кто-то со свечой), затем свет
    // расходится по соседним комнатам; мелкие окна цоколя — после удара.
    const big = all.filter((w) => w.area >= 30);
    const small = all.filter((w) => w.area < 30);
    const c0 = best ?? big[0];
    const dist = (w: WindowRect): number => (c0 ? Math.hypot(w.cx - c0.cx, (w.cy - c0.cy) * 1.4) : 0);
    big.sort((a, b) => dist(a) - dist(b));
    const r = rng(9);
    this.lights = [];
    big.forEach((w, i) => {
      // до удара — главное и ещё 4 окна, после — дом «просыпается» быстро
      const on = i < 5 ? 2.3 + i * 0.5 + r() * 0.12 : CUE.hitA + 0.3 + (i - 5) * 0.16 + r() * 0.08;
      this.lights.push({ r: w, on, seed: i + 1 });
    });
    small.forEach((w, i) => this.lights.push({ r: w, on: CUE.hitA + 1.2 + i * 0.08 + r() * 0.2, seed: 40 + i }));
  }

  private setupDebris(): void {
    const r = rng(404);
    this.debris = [];
    for (let i = 0; i < 46; i++) {
      const ang = -Math.PI * (0.08 + r() * 0.84);
      const sp = 70 + r() * 190;
      this.debris.push({
        x: (r() - 0.5) * 0.95,
        y: 0.2 + r() * 0.7,
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp,
        size: r() < 0.25 ? 2 : 1,
        ember: r() < 0.3,
      });
    }
  }

  // ─── размер ─────────────────────────────────────────────────────────

  /** Подогнать низкое разрешение под экран: ~360 строк, в портрете ≥300 по ширине. */
  resize(cssW: number, cssH: number): void {
    const aspect = Math.max(0.3, cssW / Math.max(1, cssH));
    let vh = Math.max(360, 300 / aspect);
    vh = Math.min(vh, 700);
    const vw = Math.round(vh * aspect);
    vh = Math.round(vh);
    if (vw === this.W && vh === this.H && this.canvas.width === vw) return;
    this.W = vw;
    this.H = vh;
    this.canvas.width = vw;
    this.canvas.height = vh;
    this.g = ctx2d(this.canvas);
    this.vignette = null;
  }

  // ─── проекция ───────────────────────────────────────────────────────

  private scaleAt(depth: number, cam: Cam): number {
    return D0 / Math.max(0.5, depth - cam.z);
  }

  /** Нарисовать картинку слоя: якорь (ax,ay) в пикселях картинки стоит в (wx,wy) мира. */
  private drawWorld(img: Img, depth: number, cam: Cam, wx: number, wy: number, ax: number, ay: number, unit = 1, alpha = 1, flipX = false): void {
    if (alpha <= 0.003) return;
    const s = this.scaleAt(depth, cam) * unit;
    const iw = img.width * s;
    const ih = img.height * s;
    const cx = this.W / 2 + (wx - cam.x) * this.scaleAt(depth, cam);
    const cy = this.H / 2 + (wy - cam.y) * this.scaleAt(depth, cam);
    const x = Math.round(cx - ax * s);
    const y = Math.round(cy - ay * s);
    if (x > this.W || y > this.H || x + iw < 0 || y + ih < 0) return;
    const g = this.g;
    g.globalAlpha = alpha;
    if (flipX) {
      g.save();
      g.translate(x + Math.round(iw), y);
      g.scale(-1, 1);
      g.drawImage(img, 0, 0, Math.round(iw), Math.round(ih));
      g.restore();
    } else {
      g.drawImage(img, x, y, Math.round(iw), Math.round(ih));
    }
    g.globalAlpha = 1;
  }

  private project(depth: number, cam: Cam, wx: number, wy: number): { x: number; y: number; s: number } {
    const s = this.scaleAt(depth, cam);
    return { x: this.W / 2 + (wx - cam.x) * s, y: this.H / 2 + (wy - cam.y) * s, s };
  }

  // ─── кадр ───────────────────────────────────────────────────────────

  /** Экран ожидания «нажмите»: дождь и редкие зарницы в темноте. */
  drawIdle(t: number): void {
    const g = this.g;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalCompositeOperation = "source-over";
    g.fillStyle = "#020205";
    g.fillRect(0, 0, this.W, this.H);
    const cyc = t % 5.3;
    const fl = cyc < 0.08 ? 0.22 : cyc > 0.14 && cyc < 0.2 ? 0.14 : 0;
    if (fl > 0) {
      this.drawSky({ z: 0, x: 0, y: -96 }, t, fl, true);
      g.fillStyle = "rgba(2,2,5,0.55)";
      g.fillRect(0, 0, this.W, this.H);
    }
    this.drawRain(t, 0.45, fl);
    this.drawVignette();
  }

  draw(t: number): void {
    const g = this.g;
    const W = this.W;
    const H = this.H;
    const cam = cameraAt(t);
    const { flash, backlight } = flashAt(t);

    // тряска: на ударах, детерминированная
    const shakeA = t >= CUE.hitA ? 3 * Math.exp(-(t - CUE.hitA) / 0.22) : 0;
    const shakeB = t >= CUE.hitB ? 10 * Math.exp(-(t - CUE.hitB) / 0.32) : 0;
    const amp = this.reducedMotion ? 0 : shakeA + shakeB;
    const fr = Math.floor(t * 60);
    const sx = Math.round((hash1(fr * 1.7) - 0.5) * 2 * amp);
    const sy = Math.round((hash1(fr * 2.3 + 5) - 0.5) * 2 * amp);
    g.setTransform(1, 0, 0, 1, sx, sy);
    g.globalCompositeOperation = "source-over";

    g.fillStyle = "#04040b";
    g.fillRect(-20, -20, W + 40, H + 40);

    // 1. Небо, облака, молнии позади дома
    this.drawSky(cam, t, flash, false);
    this.drawBolts(cam, t);

    // 2. Дальний лес на линии горизонта
    const hz = H / 2;
    const hs = 1 + cam.z * 0.0016;
    const hw = this.hills.width * hs;
    const hx = Math.round(W / 2 - hw / 2 - cam.x * 0.25);
    g.drawImage(this.hills, hx, Math.round(hz - this.hills.height * hs * 0.62), Math.round(hw), Math.round(this.hills.height * hs));
    g.fillStyle = "rgb(12,10,27)";
    g.fillRect(-20, Math.round(hz + this.hills.height * hs * 0.36), W + 40, H);
    this.drawFog(0, cam, t, 330, -6, 0.6, flash);

    // 3. Дом: тёмный, окна, вспышка (контровая — силуэт)
    const mAlpha = 1;
    this.drawWorld(this.mansion, DEPTH.mansion, cam, 0, 0, 128, 226, 1, mAlpha);
    this.drawWindows(cam, t);
    if (flash > 0.02) {
      if (backlight > 0) {
        this.drawWorld(this.mansionSil, DEPTH.mansion, cam, 0, 0, 128, 226, 1, backlight);
      } else {
        this.drawWorld(this.mansionLit, DEPTH.mansion, cam, 0, 0, 128, 226, 1, flash * 0.75);
      }
    }
    this.drawFigure(cam, t);
    this.drawCircling(cam, t);

    // 4. Туман у подножия, могилы, деревья
    this.drawFog(1, cam, t, 192, 8, 0.6, flash);
    const treeA = 1;
    this.drawWorld(this.tree, DEPTH.trees, cam, -170, 6, 64, 168, 1.05, treeA);
    this.drawWorld(this.tree, DEPTH.trees + 6, cam, 185, 6, 64, 168, 0.95, treeA, true);
    if (flash > 0.02 && backlight === 0) {
      this.drawWorld(this.treeLit, DEPTH.trees, cam, -170, 6, 64, 168, 1.05, flash * 0.5);
      this.drawWorld(this.treeLit, DEPTH.trees + 6, cam, 185, 6, 64, 168, 0.95, flash * 0.5, true);
    }
    // кладбище по обе стороны дорожки к дому
    const gh = this.graves.height;
    for (const [gx, flip] of [[-150, false], [160, true]] as Array<[number, boolean]>) {
      this.drawWorld(this.graves, DEPTH.graves, cam, gx, 10, 184, gh, 0.5, 1, flip);
      if (flash > 0.02 && backlight === 0) this.drawWorld(this.gravesLit, DEPTH.graves, cam, gx, 10, 184, gh, 0.5, flash * 0.6, flip);
    }
    this.drawFog(2, cam, t, 140, 12, 0.5, flash);

    // 5. Вороны за воротами
    this.drawRavens(cam, t, flash, DEPTH.gate, 1e9);

    // 6. Ворота: рама и створки (распахиваются)
    this.drawGate(cam, t, flash, backlight);
    this.drawFog(1, cam, t, 92, 26, 0.55, flash);
    // вороны ближе ворот — поверх них
    this.drawRavens(cam, t, flash, -1e9, DEPTH.gate);

    // 7. Дождь, вспышка, виньетка
    this.drawRain(t, 1, flash);
    if (flash > 0.02) {
      g.globalCompositeOperation = "lighter";
      g.fillStyle = `rgba(150,170,230,${(flash * 0.32).toFixed(3)})`;
      g.fillRect(-20, -20, W + 40, H + 40);
      g.globalCompositeOperation = "source-over";
    }
    this.drawVignette();

    // 8. Логотип поверх всего
    this.drawLogo(t, flash);

    // проявление из черноты
    const fadeIn = smooth((t - CUE.fadeInStart) / (CUE.fadeInEnd - CUE.fadeInStart));
    const blackA = 1 - fadeIn;
    // в чёрном начале вспышки всё равно пробиваются — как зарница сквозь веки
    const reveal = Math.max(0, blackA - flash * 0.6);
    if (reveal > 0.004) {
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.fillStyle = `rgba(0,0,0,${reveal.toFixed(3)})`;
      g.fillRect(0, 0, W, H);
    }
    g.setTransform(1, 0, 0, 1, 0, 0);
  }

  // ─── слои ───────────────────────────────────────────────────────────

  /** Градиент неба со звёздами: строится под размер экрана один раз. */
  private skyCanvas(): Canvas {
    if (this.skyBase && this.skyBase.width === this.W && this.skyBase.height === this.H) return this.skyBase;
    const c = makeCanvas(this.W, this.H);
    const g = ctx2d(c);
    const img = g.createImageData(this.W, this.H);
    const top: [number, number, number] = [6, 6, 18];
    const bot: [number, number, number] = [40, 42, 78];
    const steps = 7;
    for (let y = 0; y < this.H; y++) {
      const u = Math.min(1, y / (this.H * 0.62));
      for (let x = 0; x < this.W; x++) {
        // ступенчатый градиент с дизерингом на стыках ступеней
        const f = u * steps;
        const lv = Math.min(steps, Math.floor(f) + (f - Math.floor(f) > bayer(x, y) ? 1 : 0)) / steps;
        const i = (y * this.W + x) * 4;
        img.data[i] = top[0] + (bot[0] - top[0]) * lv;
        img.data[i + 1] = top[1] + (bot[1] - top[1]) * lv;
        img.data[i + 2] = top[2] + (bot[2] - top[2]) * lv;
        img.data[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    const r = rng(3);
    for (let k = 0; k < (this.W * this.H) / 900; k++) {
      const x = Math.floor(r() * this.W);
      const y = Math.floor(r() * this.H * 0.55);
      const b = 90 + Math.floor(r() * 110);
      g.fillStyle = `rgb(${b},${b},${Math.min(255, b + 40)})`;
      g.fillRect(x, y, 1, 1);
    }
    this.skyBase = c;
    return c;
  }

  private drawSky(cam: Cam, t: number, flash: number, idle: boolean): void {
    const g = this.g;
    const W = this.W;
    const H = this.H;
    g.drawImage(this.skyCanvas(), 0, 0);
    // луна: справа над шпилем, почти неподвижна (дальний план)
    const ms = 1 + cam.z * 0.0012;
    const mw = Math.round(this.moon.width * ms);
    const moonX = W / 2 + Math.min(W * 0.2, 140) - cam.x * 0.08;
    const moonY = H / 2 - 122 - (cam.y + 92) * 0.3 - cam.z * 0.18;
    // ореол
    g.globalCompositeOperation = "lighter";
    const grd = g.createRadialGradient(moonX, moonY, mw * 0.4, moonX, moonY, mw * 2.2);
    grd.addColorStop(0, `rgba(150,160,170,${(0.2 + flash * 0.2).toFixed(3)})`);
    grd.addColorStop(1, "rgba(150,160,170,0)");
    g.fillStyle = grd;
    g.fillRect(moonX - mw * 2.2, moonY - mw * 2.2, mw * 4.4, mw * 4.4);
    g.globalCompositeOperation = "source-over";
    g.drawImage(this.moon, Math.round(moonX - mw / 2), Math.round(moonY - mw / 2), mw, mw);
    if (flash > 0.02) {
      // небо вспыхивает целиком
      g.globalCompositeOperation = "lighter";
      g.fillStyle = `rgba(120,135,200,${(flash * 0.7).toFixed(3)})`;
      g.fillRect(-20, -20, W + 40, H + 40);
      g.globalCompositeOperation = "source-over";
    }
    if (idle) return;
    // облака: высокий слой медленнее, средний — быстрее и закрывает луну
    const layers: Array<[Canvas, Canvas, number, number, number]> = [
      [this.cloudsHigh, this.cloudsHighLit, 4, 0.02, 1.0],
      [this.clouds, this.cloudsLit, 10, 0.17, 1.3],
    ];
    for (const [img, lit, speed, yFrac, scl] of layers) {
      const sc = scl * (1 + cam.z * 0.0015);
      const w = img.width * sc;
      const h = Math.round(img.height * sc);
      const off = ((t * speed) % w + w) % w;
      const yy = Math.round(H * yFrac + (H / 2 - 180) * 0.35 - cam.z * 0.1);
      for (let xx = -off - cam.x * 0.1; xx < W + 20; xx += w) {
        g.drawImage(img, Math.round(xx), yy, Math.round(w), h);
        if (flash > 0.04) {
          g.globalAlpha = Math.min(1, flash);
          g.drawImage(lit, Math.round(xx), yy, Math.round(w), h);
          g.globalAlpha = 1;
        }
      }
    }
  }

  private drawBolts(cam: Cam, t: number): void {
    const g = this.g;
    for (const s of STRIKES) {
      if (s.boltX === null) continue;
      const lv = strikeLevel(s, t);
      if (lv < 0.08 || t - s.t > 0.6) continue;
      const bolt = this.bolts.get(s.seed);
      if (!bolt) continue;
      // точка удара — за домом, на горизонте
      const p = this.project(DEPTH.mansion + 160, cam, s.boltX, -30);
      const top = -8;
      const hgt = p.y - top;
      const span = hgt * 0.9;
      // ореол: широкое мягкое свечение у ствола
      g.globalCompositeOperation = "lighter";
      const grd = g.createRadialGradient(p.x, p.y - hgt * 0.4, 2, p.x, p.y - hgt * 0.4, hgt * 0.7);
      grd.addColorStop(0, `rgba(120,140,255,${(0.35 * lv).toFixed(3)})`);
      grd.addColorStop(1, "rgba(120,140,255,0)");
      g.fillStyle = grd;
      g.fillRect(p.x - hgt, top, hgt * 2, hgt * 1.1);
      for (const pass of [0, 1]) {
        for (const [x0, y0, x1, y1, lvl] of bolt.segs) {
          if (pass === 0) {
            g.fillStyle = `rgba(110,130,255,${(0.55 * lv / (1 + lvl)).toFixed(3)})`;
            pixelLine(g, p.x + x0 * span - 1, top + y0 * hgt, p.x + x1 * span - 1, top + y1 * hgt, lvl === 0 ? 3 : 2);
          } else {
            g.fillStyle = lvl === 0 ? `rgba(255,255,255,${lv.toFixed(3)})` : `rgba(210,220,255,${(lv * 0.8).toFixed(3)})`;
            pixelLine(g, p.x + x0 * span, top + y0 * hgt, p.x + x1 * span, top + y1 * hgt, 1);
          }
        }
      }
      g.globalCompositeOperation = "source-over";
    }
  }

  private drawFog(i: number, cam: Cam, t: number, depth: number, wy: number, alpha: number, flash: number): void {
    const near = depth - cam.z;
    if (near < 6) return;
    const fade = Math.min(1, (near - 6) / 14);
    const img = this.fogs[i];
    const s = this.scaleAt(depth, cam) * 1.4;
    const w = img.width * s;
    const h = img.height * s;
    const p = this.project(depth, cam, 0, wy);
    const y = Math.round(p.y - h * 0.7);
    if (y > this.H || y + h < 0) return;
    const speed = 7 + i * 5;
    const off = (((t * speed + depth * 3.1) % w) + w) % w;
    const g = this.g;
    g.globalAlpha = alpha * fade * (1 + flash * 0.4);
    for (let x = Math.round(p.x - Math.ceil(p.x / w + 1) * w + off); x < this.W; x += Math.round(w)) {
      g.drawImage(img, x, y, Math.round(w), Math.round(h));
    }
    g.globalAlpha = 1;
  }

  private windowLevel(l: WinLight, t: number): number {
    if (t < l.on) return 0;
    let v = smooth((t - l.on) / 0.22) * candle(t, l.seed);
    // загорание с «чирком спички»: короткая вспышка в начале
    if (t - l.on < 0.08) v *= 1.3;
    const isMain = this.figureWin === l.r;
    if (t >= CUE.lightsOut && t < CUE.hitB) {
      // затишье: всё гаснет (с миганием), кроме главного окна
      const k = t - CUE.lightsOut;
      if (isMain) {
        if (t >= CUE.figureOut) return 0;
        return v * (0.75 + 0.25 * Math.sin(t * 31));
      }
      if (k < 0.12) return v * (k < 0.05 ? 0.2 : 0.8);
      return 0;
    }
    return v;
  }

  private drawWindows(cam: Cam, t: number): void {
    const g = this.g;
    const p = this.project(DEPTH.mansion, cam, 0, 0);
    const s = p.s;
    const ox = p.x - 128 * s;
    const oy = p.y - 226 * s;
    const red = t >= CUE.hitB;
    const src = red ? this.winRed : this.win;
    for (const l of this.lights) {
      let lv = this.windowLevel(l, t);
      if (red) lv = Math.max(lv, 0.75 + 0.25 * candle(t * 1.7, l.seed + 50));
      if (lv <= 0.01) continue;
      const r = l.r;
      const dx = Math.round(ox + r.x * s);
      const dy = Math.round(oy + r.y * s);
      g.globalAlpha = Math.min(1, lv);
      g.drawImage(src, r.x, r.y, r.w, r.h, dx, dy, Math.max(1, Math.round(r.w * s)), Math.max(1, Math.round(r.h * s)));
      g.globalAlpha = 1;
      // ореол света в тумане
      g.globalCompositeOperation = "lighter";
      const cx = ox + r.cx * s;
      const cy = oy + r.cy * s;
      const rad = (Math.max(r.w, r.h) * 1.6 + 4) * s;
      const grd = g.createRadialGradient(cx, cy, 1, cx, cy, rad);
      const col = red ? "200,20,10" : "255,130,40";
      grd.addColorStop(0, `rgba(${col},${(0.22 * lv).toFixed(3)})`);
      grd.addColorStop(1, `rgba(${col},0)`);
      g.fillStyle = grd;
      g.fillRect(cx - rad, cy - rad, rad * 2, rad * 2);
      g.globalCompositeOperation = "source-over";
    }
  }

  /** Силуэт в главном окне во время затишья. */
  private drawFigure(cam: Cam, t: number): void {
    const w = this.figureWin;
    if (!w || t < CUE.figureIn || t >= CUE.figureOut) return;
    const g = this.g;
    const p = this.project(DEPTH.mansion, cam, 0, 0);
    const s = p.s;
    const ox = p.x - 128 * s;
    const oy = p.y - 226 * s;
    const appear = smooth((t - CUE.figureIn) / 0.35);
    const cx = ox + w.cx * s;
    const bottom = oy + (w.y + w.h) * s;
    const hh = w.h * 0.62 * s;
    g.globalAlpha = appear;
    g.fillStyle = "rgb(4,3,8)";
    // голова, плечи, туловище — пиксельными прямоугольниками
    const u = Math.max(1, Math.round(s));
    const head = Math.max(2, Math.round(hh * 0.26));
    const hx = Math.round(cx - head / 2);
    const hy = Math.round(bottom - hh);
    g.fillRect(hx, hy, head, head);
    g.fillRect(Math.round(cx - head * 0.9), hy + head, Math.round(head * 1.8), Math.round(hh - head));
    g.fillRect(Math.round(cx - head * 1.15), hy + head + u, Math.round(head * 2.3), Math.round(hh - head - u));
    g.globalAlpha = 1;
  }

  private drawCircling(cam: Cam, t: number): void {
    if (t > CUE.hitA + 0.1) return;
    for (const c of this.circling) {
      const a = t * c.sp + c.ph;
      const wx = c.cx + Math.cos(a) * c.r;
      const wy = c.cy + Math.sin(a) * c.r * 0.35;
      const p = this.project(DEPTH.mansion + 20, cam, wx, wy);
      const fr = this.ravenImg[Math.floor(t * 6 + c.ph * 3) % 3];
      const sz = Math.max(1, p.s * 0.55);
      this.g.drawImage(fr, Math.round(p.x), Math.round(p.y), Math.round(fr.width * sz), Math.round(fr.height * sz));
    }
  }

  private drawRavens(cam: Cam, t: number, flash: number, minD: number, maxD: number): void {
    const g = this.g;
    for (const r of this.ravens) {
      const dt = t - r.t0;
      if (dt < 0 || dt > 6) continue;
      const wob = Math.sin(dt * 3 + r.phase) * 6;
      const wx = r.x + r.vx * dt + wob;
      const wy = r.y + r.vy * dt + Math.sin(dt * 2.3 + r.phase) * 4;
      const d = r.d + r.vd * dt;
      if (d - cam.z < 4 || d <= minD || d > maxD) continue;
      const p = this.project(d, cam, wx, wy);
      const sz = p.s * 1.1;
      const fr = this.ravenImg[Math.floor(dt * r.flap + r.phase) % 3];
      const w = Math.max(2, Math.round(fr.width * sz));
      const h = Math.max(1, Math.round(fr.height * sz));
      const x = Math.round(p.x - w / 2);
      const y = Math.round(p.y - h / 2);
      if (x > this.W || y > this.H || x + w < 0 || y + h < 0) continue;
      g.drawImage(fr, x, y, w, h);
      // во вспышку вороны — резкие чёрные силуэты; без неё слегка тонут в тумане
      if (flash < 0.2 && d - cam.z > 60) {
        g.globalAlpha = 0.25;
        g.fillStyle = "rgb(30,34,60)";
        g.fillRect(x, y, w, h);
        g.globalAlpha = 1;
      }
    }
  }

  private drawGate(cam: Cam, t: number, flash: number, backlight: number): void {
    const depth = DEPTH.gate;
    const near = depth - cam.z;
    if (near < 3) return;
    const unit = 0.55;
    const ax = 128;
    const ay = 244;
    const wy = 6;
    // створки: петли у внутренних столбов (x=77 и x=180 в картинке)
    const open = smooth((t - CUE.gateOpenStart) / (CUE.gateOpenEnd - CUE.gateOpenStart));
    // открываются внутрь: ширина створки ≈ cos(угла), с «дёрганьем» в начале
    const jerk = t > CUE.gateOpenStart && t < CUE.gateOpenStart + 0.4 ? Math.sin((t - CUE.gateOpenStart) * 40) * 0.015 : 0;
    const k = Math.max(0.06, Math.cos(open * 1.38) + jerk);
    const s = this.scaleAt(depth, cam) * unit;
    const p = this.project(depth, cam, 0, wy);
    const left = p.x - ax * s;
    const top = p.y - ay * s;
    const g = this.g;
    const dark = Math.min(1, 0.2 + open * 0.6); // повёрнутая створка темнее
    const halves: Array<[number, number, number]> = [
      [77, 128, 77], // от x=77 до 128, петля слева
      [128, 180, 180], // от 128 до 180, петля справа
    ];
    for (const [sx0, sx1, hinge] of halves) {
      const sw = sx1 - sx0;
      const dw = sw * s * k;
      const hx = left + hinge * s;
      const dx = hinge === sx0 ? hx : hx - dw;
      const sy0 = 100;
      const sh = 144;
      const dy = Math.round(top + sy0 * s);
      const dh = Math.round(sh * s);
      if (dw < 0.6) continue;
      g.drawImage(this.doors, sx0, sy0, sw, sh, Math.round(dx), dy, Math.max(1, Math.round(dw)), dh);
      if (flash > 0.02 && backlight === 0) {
        g.globalAlpha = flash * 0.55;
        g.drawImage(this.doorsLit, sx0, sy0, sw, sh, Math.round(dx), dy, Math.max(1, Math.round(dw)), dh);
        g.globalAlpha = 1;
      }
      // повёрнутая створка уходит в тень
      const shade = backlight > 0 ? 1 : dark * open * 0.6;
      if (shade > 0.02) {
        g.globalAlpha = shade;
        g.drawImage(this.doorsSil, sx0, sy0, sw, sh, Math.round(dx), dy, Math.max(1, Math.round(dw)), dh);
        g.globalAlpha = 1;
      }
    }
    this.drawWorld(this.gate, depth, cam, 0, wy, ax, ay, unit);
    // вблизи ворота уходят в тень: крупные пиксели читаются как силуэт
    const close = smooth((s - 2.6) / 2.4);
    if (close > 0.01 && backlight === 0) {
      this.drawWorld(this.gateSil, depth, cam, 0, wy, ax, ay, unit, close * 0.85);
    }
    if (flash > 0.02) {
      if (backlight > 0) {
        // контровой свет: рама — чёрный силуэт
        this.drawWorld(this.gateSil, depth, cam, 0, wy, ax, ay, unit, backlight);
      } else {
        this.drawWorld(this.gateLit, depth, cam, 0, wy, ax, ay, unit, flash * 0.55);
      }
    }
  }

  private drawRain(t: number, strength: number, flash: number): void {
    const g = this.g;
    const speeds = [150, 260, 430];
    const alphas = [0.4, 0.5, 0.5];
    for (let i = 0; i < this.rain.length; i++) {
      const img = this.rain[i];
      const sc = 1 + i * 0.5;
      const size = Math.round(256 * sc);
      const oy = (t * speeds[i]) % size;
      const ox = (t * speeds[i] * 0.22) % size;
      g.globalAlpha = Math.min(1, alphas[i] * strength * (1 + flash * 0.8));
      for (let y = -size + oy; y < this.H; y += size) {
        for (let x = -size + ox; x < this.W; x += size) {
          g.drawImage(img, Math.round(x), Math.round(y), size, size);
        }
      }
    }
    g.globalAlpha = 1;
  }

  private drawVignette(): void {
    if (!this.vignette || this.vignette.width !== this.W || this.vignette.height !== this.H) {
      const c = makeCanvas(this.W, this.H);
      const g = ctx2d(c);
      const img = g.createImageData(this.W, this.H);
      const cx = this.W / 2;
      const cy = this.H / 2;
      const rx = this.W * 0.62;
      const ry = this.H * 0.7;
      for (let y = 0; y < this.H; y++) {
        for (let x = 0; x < this.W; x++) {
          const d = Math.hypot((x - cx) / rx, (y - cy) / ry);
          const v = Math.max(0, d - 0.62) * 1.6;
          if (v > bayer(x, y)) {
            const i = (y * this.W + x) * 4;
            img.data[i + 3] = Math.round(Math.min(1, v) * 150);
          }
        }
      }
      g.putImageData(img, 0, 0);
      this.vignette = c;
    }
    this.g.drawImage(this.vignette, 0, 0);
  }

  private drawLogo(t: number, flash: number): void {
    const t0 = CUE.hitB - LOGO_FLY;
    if (t < t0) return;
    const g = this.g;
    const W = this.W;
    const H = this.H;
    // большой логотип, если влезает с полями; иначе малый (и при нужде — меньше 1:1)
    const big = this.logoBig.width <= W * 0.8;
    const img = big ? this.logoBig : this.logoSmall;
    const v = this.logoVariants.get(img);
    if (!v) return;
    const fit = Math.min(1, (W * 0.9) / img.width);
    const dt = t - t0;
    // полёт: из огромного в 1:1 с ускорением, затем отскок
    let sc: number;
    if (dt < LOGO_FLY) {
      const u = dt / LOGO_FLY;
      sc = 1 + 3.4 * (1 - u) * (1 - u);
    } else {
      const a = dt - LOGO_FLY;
      sc = 1 - 0.07 * Math.exp(-a / 0.09) * Math.cos(a * 38) + Math.min(0.045, a * 0.012);
    }
    sc *= fit;
    const w = Math.round(img.width * sc);
    const h = Math.round(img.height * sc);
    const cx = W / 2;
    const cy = H * (H > W ? 0.3 : 0.36);
    const x = Math.round(cx - w / 2);
    const y = Math.round(cy - h / 2);
    const landed = dt >= LOGO_FLY;
    const since = dt - LOGO_FLY;

    // тёмная подложка: логотип читается поверх светящихся окон
    if (landed) {
      const back = Math.min(1, since / 0.25);
      const grd0 = g.createRadialGradient(cx, cy, h * 0.2, cx, cy, w * 0.62);
      grd0.addColorStop(0, `rgba(0,0,0,${(0.6 * back).toFixed(3)})`);
      grd0.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = grd0;
      g.save();
      g.translate(cx, cy);
      g.scale(1, 0.45);
      g.translate(-cx, -cy);
      g.fillRect(cx - w, cy - w, w * 2, w * 2);
      g.restore();
    }
    // красное зарево за логотипом
    if (landed) {
      g.globalCompositeOperation = "lighter";
      const pulse = 0.7 + 0.3 * Math.sin(since * 3.1);
      const grd = g.createRadialGradient(cx, cy, 4, cx, cy, w * 0.62);
      grd.addColorStop(0, `rgba(150,16,8,${(0.32 * pulse).toFixed(3)})`);
      grd.addColorStop(1, "rgba(150,16,8,0)");
      g.fillStyle = grd;
      g.fillRect(cx - w, cy - w, w * 2, w * 2);
      g.globalCompositeOperation = "source-over";
    }

    // шлейф при полёте
    if (!landed) {
      g.globalAlpha = 0.25;
      for (const k of [1.25, 1.55]) {
        const ww = Math.round(w * k);
        const hh = Math.round(h * k);
        g.drawImage(img, Math.round(cx - ww / 2), Math.round(cy - hh / 2), ww, hh);
      }
      g.globalAlpha = 1;
    }

    // тень под буквами для читаемости
    g.globalAlpha = 0.7;
    g.drawImage(this.logoShadow(img), x + Math.max(1, Math.round(2 * sc)), y + Math.max(1, Math.round(3 * sc)), w, h);
    g.globalAlpha = 1;
    g.drawImage(img, x, y, w, h);
    // сталь букв чуть светлее исходника — иначе тонет в ночи
    g.globalAlpha = 0.28;
    g.drawImage(v.lit, x, y, w, h);
    g.globalAlpha = 1;

    if (landed) {
      // хроматическое расслоение в момент удара
      const ca = Math.exp(-since / 0.12);
      if (ca > 0.03) {
        g.globalCompositeOperation = "lighter";
        g.globalAlpha = ca * 0.7;
        const off = Math.round(3 * ca) + 1;
        g.drawImage(v.red, x - off, y, w, h);
        g.drawImage(v.cyan, x + off, y, w, h);
        g.globalAlpha = 1;
        g.globalCompositeOperation = "source-over";
      }
      // белый оттиск в первые кадры — «удар»
      const white = Math.max(0, 1 - since / 0.07);
      if (white > 0) {
        g.globalAlpha = white;
        g.drawImage(v.sil, x, y, w, h);
        g.globalAlpha = 1;
      }
      // угли в трещинах дышат
      g.globalCompositeOperation = "lighter";
      g.globalAlpha = 0.45 + 0.35 * Math.sin(since * 4.2) * Math.sin(since * 1.3 + 1);
      g.drawImage(v.ember, x, y, w, h);
      g.globalAlpha = 1;
      g.globalCompositeOperation = "source-over";
      // молния освещает буквы
      if (flash > 0.05) {
        g.globalAlpha = flash * 0.55;
        g.drawImage(v.lit, x, y, w, h);
        g.globalAlpha = 1;
      }
      this.drawImpact(cx, cy, w, h, since);
    }
  }

  private shadowCache = new Map<Img, Canvas>();
  private logoShadow(img: Img): Canvas {
    let c = this.shadowCache.get(img);
    if (!c) {
      c = silhouette(img, [0, 0, 0]);
      this.shadowCache.set(img, c);
    }
    return c;
  }

  /** Ударная волна, обломки и искры от логотипа. */
  private drawImpact(cx: number, cy: number, w: number, h: number, since: number): void {
    const g = this.g;
    // кольцо ударной волны (эллипс по пикселям)
    if (since < 0.5) {
      const u = since / 0.5;
      const rx = w * (0.45 + u * 0.75);
      const ry = h * (0.6 + u * 1.1);
      g.fillStyle = `rgba(220,225,255,${(0.5 * (1 - u)).toFixed(3)})`;
      const n = Math.round(rx * 2.2);
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        g.fillRect(Math.round(cx + Math.cos(a) * rx), Math.round(cy + Math.sin(a) * ry), 1, 1);
      }
    }
    // обломки камня и угольки: баллистика от нижней кромки букв
    if (since < 3) {
      const grav = 340;
      for (const d of this.debris) {
        const x = cx + d.x * w + d.vx * since;
        const y = cy + (d.y - 0.5) * h + d.vy * since + 0.5 * grav * since * since;
        if (y > this.H + 4) continue;
        const life = d.ember ? Math.max(0, 1 - since / 1.6) : 1;
        if (life <= 0) continue;
        g.globalAlpha = life;
        g.fillStyle = d.ember ? (Math.floor(since * 20 + d.vx) % 2 ? "rgb(255,120,40)" : "rgb(220,40,20)") : "rgb(96,100,118)";
        g.fillRect(Math.round(x), Math.round(y), d.size, d.size);
      }
      g.globalAlpha = 1;
    }
    // искры, поднимающиеся от углей, пока логотип висит
    const r = rng(Math.floor(since * 6));
    for (let i = 0; i < 6; i++) {
      const life = (since * 0.7 + i / 6) % 1;
      const x = cx + (hash1(i * 3.3 + Math.floor(since * 0.7 + i / 6) * 9.1) - 0.5) * w * 0.9;
      const y = cy + h * 0.35 - life * h * 1.6;
      g.globalAlpha = (1 - life) * 0.9;
      g.fillStyle = r() < 0.5 ? "rgb(255,110,40)" : "rgb(255,60,30)";
      g.fillRect(Math.round(x + Math.sin(life * 9 + i) * 3), Math.round(y), 1, 1);
    }
    g.globalAlpha = 1;
  }

  /** Отладка: окна и главное окно (для подбора). */
  debugInfo(): unknown {
    return { windows: this.lights.map((l) => ({ ...l.r, on: l.on })), figure: this.figureWin, W: this.W, H: this.H };
  }
}
